import React, { useCallback, useEffect } from "react";
import SeoHead from "../components/SeoHead";
import { useLocation, Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Marquee from "../components/Marquee";
import YMonogram from "../components/YMonogram";
import Hero from "../components/sections/Hero";
import ServicesGrid from "../components/sections/ServicesGrid";
import ClientsMarquee from "../components/sections/ClientsMarquee";
import WorksGrid from "../components/sections/WorksGrid";
import ReviewsSlider from "../components/sections/ReviewsSlider";
import StatsBand from "../components/sections/StatsBand";
import ProcessSteps from "../components/sections/ProcessSteps";
import CTASection from "../components/sections/CTASection";
import WorksMegaCta from "../components/sections/WorksMegaCta";
import WebPreviewStrip from "../components/sections/WebPreviewStrip";
import BlogPreviewStrip from "../components/sections/BlogPreviewStrip";
import { useLanguage } from "../contexts/LanguageContext";
import { dictionaries } from "../i18n";

const MARQUEE_TOP = [
  "META ADS", "GOOGLE ADS", "SEO", "POSTUN HAZIR",
  "CREATIVE", "SOCIAL", "PERFORMANS", "GLOBAL",
  "6 LANG", "15+ YEARS", "200+ PROJECTS",
];

const MARQUEE_VALUES = [
  "DATA-DRIVEN", "PERFORMANCE-DRIVEN",
  "GLOBAL-READY", "CONVERSION-FIRST",
  "CREATIVE STRATEGY",
];

export default function HomePage() {
  const { lang } = useLanguage();
  const dict = dictionaries[lang];
  const location = useLocation();

  const scrollToContact = useCallback(() => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Handle scroll-to from other pages via navigation state
  useEffect(() => {
    if (location.state?.scrollTo) {
      const target = document.querySelector(`[data-testid="${location.state.scrollTo}"]`) ||
                     document.getElementById(location.state.scrollTo);
      if (target) {
        // Small delay to ensure DOM is ready
        const t = setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
          // Flash effect
          target.classList.add("budget-flash");
          setTimeout(() => target.classList.remove("budget-flash"), 1500);
        }, 300);
        return () => clearTimeout(t);
      }
    }
  }, [location.state]);

  const pageTitle = `Yastık Medya · ${dict?.hero?.eyebrow ?? ""}`;
  const pageDesc = dict?.hero?.subtitle ?? "";

  return (
    <div data-testid="home-page" className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description={pageDesc} path={`/${lang}/`} imageKey="home" lang={lang} themeColor="#FFF9F4" />
      <HrefLangTags />
      <Header />
      <main>
        <Hero onCtaClick={scrollToContact} />

        {/* Slim pink keyword strip */}
        <Marquee
          items={MARQUEE_TOP}
          variant="pink"
          size="thin"
          speed="normal"
          separator={<span className="opacity-90">/</span>}
          className="border-y border-ink/15"
        />

        {/* Reviews immediately after marquee — first impression / social proof */}
        <ReviewsSlider />

        <ServicesGrid />

        {/* Client logos */}
        <ClientsMarquee />

        {/* Selected works */}
        <WorksGrid />
        <WorksMegaCta />

        {/* Web portfolio preview */}
        <WebPreviewStrip />

        {/* Blog preview */}
        <BlogPreviewStrip />

        {/* Slim ink keyword strip with Y-monogram brand separator */}
        <Marquee
          items={MARQUEE_VALUES}
          variant="ink"
          size="thin"
          speed="slow"
          separator={<YMonogram size={26} id="m2" />}
        />

        <StatsBand />
        <ProcessSteps />

        {/* Package Finder CTA — links to dedicated page */}
        <section className="bg-cream py-section border-t border-mist" data-testid="package-finder-cta">
          <div className="container-page text-center">
            <span className="eyebrow justify-center mb-5">
              <Sparkles className="h-3 w-3 text-brand-pink" />
              {lang === "tr" ? "AKILLI EŞLEŞTİRME" : "SMART MATCHING"}
            </span>
            <h2 className="font-display text-display-2 font-semibold text-ink mt-5">
              {lang === "tr" ? "Markana uygun paketi " : "Find the right plan for your brand "}
              <span className="gold-foil-text">
                {lang === "tr" ? "30 saniyede bul." : "in 30 seconds."}
              </span>
            </h2>
            <p className="mt-5 text-body-lg text-ink/60 max-w-xl mx-auto">
              {lang === "tr"
                ? "3 hızlı soru. Hizmetini seç, önceliğini belirt, sana özel paketi gör."
                : "3 quick questions. Select your service, set priorities, see your personalized package."}
            </p>
            <Link
              to={`/${lang}/paketini-bul`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-pink to-brand-magenta text-cream px-8 py-4 text-base font-bold hover:scale-[1.02] transition-transform shadow-[0_16px_40px_-12px_rgba(238,79,122,0.6)]"
            >
              <Sparkles className="h-5 w-5" />
              {lang === "tr" ? "Paketini Bul — Hemen Başla" : "Find Your Plan — Start Now"}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
